import React from 'react'
import moment from 'moment'
import { useMutation } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { Badge, Button, Fieldset, Image, Modal, Paper, Table, Text } from '@mantine/core'

import { EventBaseType } from '@customTypes/event'

import QueryKeys from '@constants/queryKeys'

import DataRepo from '@api/datasource/data'
import queryClient from '@api/datasource/query'

import { useIsMobile } from '@hooks/mobile'

import { ErrorService } from '@utils/error'
import { isLoadingMutation } from '@utils/network'

import ConfirmationModal from '@components/shared/confirmationModal'
import { ModalProps } from '@customTypes/page'
import { IconCircleFilled, IconDownload } from '@tabler/icons-react'
import { DATE_FORMAT } from '@constants/app'
import NumberFormat from '@components/shared/Number'
import { downloadAttachment } from '@utils/firebase'
import { Carousel } from '@mantine/carousel'

export type EventFormType = {
  clientID?: string | null
  initialStatus?: string
  initialType?: string
  event?: EventBaseType
}

const EventPreview = (props: ModalProps<EventFormType> & { onEdit?: () => void }) => {
  const {
    open,
    modal,
    data = {} as EventFormType,
    size = 'lg',
    onEdit,
    onClose: outerOnClose,
  } = props

  const [confirmExit, setConfirmExit] = React.useState(false)

  const [confirmDelete, setConfirmDelete] = React.useState(false)

  const isMobile = useIsMobile()

  const eventDeleteMutation = useMutation<void, ErrorService, string>({
    mutationFn: async (uid) => {
      const response = await DataRepo.clientEntityService.deleteEvent(uid)

      await queryClient.invalidateQueries({
        predicate: (query) => [QueryKeys.GET_EVENTS_KEY].includes(query.queryKey[0] as string),
        refetchType: 'all',
      })

      return response
    },
    onSettled: (_, error) => {
      if (error) {
        return notifications.show({
          color: 'red',
          title: 'Error',
          message: error.message ?? 'Error al eliminar el evento',
        })
      }

      notifications.show({
        color: 'green',
        title: 'Éxito',
        message: 'Evento eliminado correctamente',
      })

      onClose?.()
    },
  })

  const event = (data.event || {}) as EventBaseType

  const attachments = event?.attachments || []

  const content = (
    <div className="cd-flex cd-flex-col cd-gap-y-[1rem]">
      <div className="cd-flex cd-flex-col cd-gap-y-[1rem] md:cd-flex-row md:cd-gap-x-[1rem] cd-justify-start">
        <Badge color={event.status?.color || 'gray'} variant="filled">
          {event.status?.name || 'Sin estado'}
        </Badge>

        <Badge color={event.type?.color || 'gray'} variant="filled">
          {event.type?.name || 'Sin tipo'}
        </Badge>
      </div>

      <Fieldset legend="Cliente">
        <Text className="cd-text-gray-600 dark:cd-text-gray-300">
          {[event.client?.name, event.client?.ruc].filter(Boolean).join(' - ')}
        </Text>
      </Fieldset>

      <Text className="cd-text-gray-600 dark:cd-text-gray-300">
        {event.description || 'Sin descripción'}
      </Text>

      <Paper withBorder>
        <Table.ScrollContainer minWidth={undefined} type="native">
          <Table striped withColumnBorders withRowBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Fecha de inicio</Table.Th>
                <Table.Th>Fecha de fin</Table.Th>
                <Table.Th>Cantidad</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>
                  <Text className="cd-text-gray-600 dark:cd-text-gray-300">
                    {moment.unix(event.date).format(DATE_FORMAT)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text className="cd-text-gray-600 dark:cd-text-gray-300">
                    {event.endDate
                      ? moment.unix(event.endDate).format(DATE_FORMAT)
                      : 'Sin fecha de fin'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text className="cd-text-gray-600 dark:cd-text-gray-300">
                    {event.amount ? (
                      <NumberFormat prefix="$" value={event.amount} />
                    ) : (
                      'Sin cantidad'
                    )}
                  </Text>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <Paper withBorder>
        <Table.ScrollContainer minWidth={undefined} type="native">
          <Table highlightOnHover striped withColumnBorders withRowBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Contacto</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Teléfono</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {Boolean(event.contacts?.length) &&
                event.contacts?.map((contact) => (
                  <Table.Tr key={contact.uid}>
                    <Table.Td>
                      <div className="cd-flex cd-items-center cd-gap-x-[0.5rem]">
                        <IconCircleFilled
                          className={contact.master ? 'cd-text-blue-500' : 'cd-text-gray-400'}
                          size={16}
                        />
                        <Text className="cd-text-gray-600 dark:cd-text-gray-300">
                          {contact.name || 'Sin nombre'}
                        </Text>
                      </div>
                    </Table.Td>
                    <Table.Td>
                      <Text className="cd-text-gray-600 dark:cd-text-gray-300">
                        {contact.email || 'Sin email'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text className="cd-text-gray-600 dark:cd-text-gray-300">
                        {contact.phone || 'Sin teléfono'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              {!event.contacts?.length && (
                <Table.Tr>
                  <Table.Td className="cd-text-center" colSpan={3}>
                    <Text className="cd-text-gray-600 dark:cd-text-gray-300">Sin contactos</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      {Boolean(attachments.length) && (
        <div>
          <Text className="cd-text-base cd-mb-[0.5rem]">Archivos adjuntos</Text>

          {attachments.length === 1 && (
            <div className="cd-flex cd-flex-col cd-justify-center cd-relative">
              <Image
                className="cd-flex-none cd-object-contain cd-h-[400px] cd-w-auto"
                radius="sm"
                src={attachments[0]}
                //onClick={() => setImageFull(attachments[0])}
              />
              <div className="cd-flex cd-justify-center cd-mt-[0.5rem]">
                <Button
                  color="green"
                  leftSection={<IconDownload size={20} />}
                  onClick={() => downloadAttachment(attachments[0], `${event.name}-1`)}
                >
                  Descargar
                </Button>
              </div>
            </div>
          )}

          {attachments.length > 1 && (
            <Carousel withControls className="control " slideGap="md" style={{ flex: 1 }}>
              {attachments.map((attachment, index) => (
                <Carousel.Slide className="cd-flex cd-justify-center" key={index}>
                  <div className="cd-flex cd-flex-col cd-justify-center cd-relative">
                    <Image
                      className="cd-flex-none cd-object-cover cd-h-[400px] cd-w-auto"
                      radius="sm"
                      src={attachment}
                      //onClick={() => setImageFull(attachment)}
                    />

                    <div className="cd-flex cd-justify-center cd-mt-[0.5rem]">
                      <Button
                        color="green"
                        leftSection={<IconDownload size={20} />}
                        onClick={() => downloadAttachment(attachment, `${event.name}-${index + 1}`)}
                      >
                        Descargar
                      </Button>
                    </div>
                  </div>
                </Carousel.Slide>
              ))}
            </Carousel>
          )}
        </div>
      )}

      <div className="cd-flex cd-flex-col cd-gap-y-[0.5rem]">
        <Button
          color="red"
          loaderProps={{ type: 'dots' }}
          loading={isLoadingMutation(eventDeleteMutation)}
          variant="subtle"
          onClick={() => {
            if (!event) return
            setConfirmDelete(true)
          }}
        >
          Eliminar evento
        </Button>
        {onEdit && (
          <Button
            color="blue"
            loaderProps={{ type: 'dots' }}
            loading={isLoadingMutation(eventDeleteMutation)}
            variant="filled"
            onClick={onEdit}
          >
            Editar evento
          </Button>
        )}
      </div>
    </div>
  )

  if (modal) {
    return (
      <Modal
        centered
        fullScreen={isMobile}
        opened={Boolean(open)}
        size={size}
        title={<Text className="cd-text-lg cd-font-bold">{event?.name}</Text>}
        onClose={onClose}
      >
        {content}
        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Descartar"
          open={confirmExit}
          title="Descartar cambios"
          onCancel={() => setConfirmExit(false)}
          onConfirm={onClose}
        >
          <Text className="cd-text-base">
            Has realizado cambios en el formulario, ¿Deseas cerrarlo y descartar los cambios?
          </Text>
        </ConfirmationModal>

        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Eliminar"
          open={confirmDelete}
          title="Eliminar evento"
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            if (!event) return
            eventDeleteMutation.mutate(event.uid)
            setConfirmDelete(false)
          }}
        >
          <Text className="cd-text-base">
            ¿Estás seguro de que deseas eliminar este evento? Esta acción no se puede deshacer.
          </Text>
        </ConfirmationModal>
      </Modal>
    )
  }

  return content

  function onClose() {
    setConfirmExit(false)
    outerOnClose?.()
  }
}

export default EventPreview
